import {useCallback, useMemo} from 'react';
import useTranslation from 'next-translate/useTranslation';

import {useConsumerCart, useDerivedMenuData} from '@/components/features/menu/hooks';
import {type PartialTypeaheadResult} from '@/components/global/layout/AppBar/SearchBar/types';
import {getTrackingProperties} from '@/components/global/layout/AppBar/SearchBar/utils';
import {useGlobalFulfillmentDetail} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {
  type DietaryOptionEnum,
  type FulfillmentDetailStrategy,
  TypeaheadDocType,
} from '@/graphql/types';
import useIdentity from '@/hooks/useIdentity';
import useTracking from '@/hooks/useTracking';
import {formatEventTimeForTracking} from '@/utils/dateFormat';
import {useEventBarV2, type WhereSelection} from '../EventBarV2Provider';
import {type EventBarField} from '../utils/fields';
import {WHAT_SUGGESTIONS} from '../utils/what';
import {type EventBarSearchHistoryEntry} from './useEventBarSearchHistory';

type TypeaheadClickArgs = {
  suggestion: PartialTypeaheadResult;
  rank: number | null;
  keyword: string;
  suggestionCount: number;
};

type TypeaheadViewedArgs = {
  caterers: PartialTypeaheadResult[];
  cuisines: PartialTypeaheadResult[];
  diversity: PartialTypeaheadResult[];
  keyword: string;
  suggestionCount: number;
};

type OrderAccountArgs = {corpAccountId: string | null; isParent: boolean | null};

type SearchHistorySelectedArgs = {
  entry: EventBarSearchHistoryEntry;
  rank: number;
  resultCount: number;
  source: 'what' | 'where';
};

// The just-picked address parts, echoed onto the address-selected events. Their keys match the
// tracking base's `street`/`city`/`state`, so spreading them overrides the still-committed (stale)
// address that getTrackingProperties derived from the not-yet-updated fulfillment detail.
type SelectedAddress = {street: string; city: string; state: string};

const pickAddressParts = ({street, city, state}: SelectedAddress): SelectedAddress => ({
  street,
  city,
  state,
});

// The staged Where address, mapped onto those same keys so an Update event reports the address the
// commit is about to persist rather than the one still on the detail. Every selection path lands on
// a structured shape (typed/prediction and recent addresses geocode, saved addresses carry their
// parts, geolocation returns a geocoded result); a bare Google prediction commits by placeId alone,
// so there are no parts to report for it.
const stagedAddressProperties = (selection: WhereSelection | null): SelectedAddress | null => {
  switch (selection?.kind) {
    case 'geocoded':
      return pickAddressParts(selection.geocoded);
    case 'geolocation':
      return pickAddressParts(selection.geocodedAddress);
    case 'address':
      return selection.address.isAutocompleteResult ? null : pickAddressParts(selection.address);
    default:
      return null;
  }
};

// The three dropdowns whose "opened" event carries the standard eventbar payload. What's
// open event (`search-bar-clicked`) has a different signature (no sub_category), so it is
// handled separately in trackFieldOpened.
const OPEN_EVENT_BY_FIELD = {
  where: 'location-details-clicked',
  when: 'date-time-clicked',
  who: 'event-details-clicked',
} as const;

/**
 * EventBarV2 tracking, mirroring SearchBar/useSearchTracking: builds the shared misc_json base
 * from getTrackingProperties and exposes one typed callback per tracked interaction. Every event
 * uses the ungoverned `track` (→ warehouse.events) so BI can query it. Call this only inside the
 * rendered EventBar components — never in the app-wide EventBarV2Provider — to keep its data
 * subscriptions out of the always-mounted provider.
 */
export const useEventBarV2Tracking = () => {
  const {t} = useTranslation('app-bar');
  const {track, trackClick} = useTracking();
  const {fulfillmentDetail} = useGlobalFulfillmentDetail();
  const consumerCart = useConsumerCart();
  const {caterer} = useDerivedMenuData();
  const {data: identityData} = useIdentity();
  const catererId = caterer?.id ?? null;

  // Locally-staged (uncommitted) EventBarV2 field state. The shared tracking base is built from the
  // committed fulfillment detail, which only updates on the Search click — so the search-button
  // event overlays these to report what the user is actually about to search with.
  const {whenDate, whenTime, headCount, eventName, fulfillmentType, timeZone, whereSelection} =
    useEventBarV2();

  const trackingProperties = useMemo(
    () =>
      getTrackingProperties({consumerCart, fulfillmentDetail, identityData: identityData ?? null}),
    [consumerCart, fulfillmentDetail, identityData],
  );

  const buildMiscJson = useCallback(
    (extras: Record<string, unknown> = {}) => JSON.stringify({...trackingProperties, ...extras}),
    [trackingProperties],
  );

  // The staged date/time/headcount/name/type the Search click is about to commit, mapped onto the
  // tracking shape and overlaid on the committed base.
  const stagedSearchProperties = useMemo(
    () => ({
      eventDate: whenDate,
      eventTime: whenTime,
      eventDateTimeLocal: formatEventTimeForTracking({
        eventOn: whenDate,
        eventLocalTime: whenTime,
        timeZoneIdentifier: timeZone,
      }),
      eventName: eventName || null,
      orderType: fulfillmentType,
      ...(headCount !== undefined && {headcountNumber: headCount}),
    }),
    [whenDate, whenTime, timeZone, eventName, fulfillmentType, headCount],
  );

  // What a per-panel Update button is about to persist: one `commitFulfillment()` flushes the whole
  // staged bar whichever button was clicked, so all three events carry the whole staged state. The
  // Where address rides on `whereSelection` (also what gates it onto the commit); trackWhereUpdate
  // handles the guard-resolved address that never reaches it.
  const stagedUpdateProperties = useMemo(
    () => ({...stagedSearchProperties, ...stagedAddressProperties(whereSelection)}),
    [stagedSearchProperties, whereSelection],
  );

  const whatDefaultSuggestions = useMemo(
    () =>
      WHAT_SUGGESTIONS.map(({key, filter}) => ({
        display: t(`eventBarV2.what.suggestions.${key}`),
        category: filter.source,
      })),
    [t],
  );

  // Panel opened (any trigger: direct click, cross-field auto-advance, or external openCommand).
  const trackFieldOpened = useCallback(
    (field: EventBarField) => {
      const page = window.location.pathname;
      if (field === 'what') {
        track('search-bar-clicked', {
          caterer_id: catererId,
          misc_json: buildMiscJson({defaultSuggestions: whatDefaultSuggestions}),
          page,
        });
        return;
      }
      track(OPEN_EVENT_BY_FIELD[field], {
        caterer_id: catererId,
        misc_json: buildMiscJson(),
        page,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track, whatDefaultSuggestions],
  );

  // Clicked a suggested-search chip in the What panel.
  const trackRecommendedSearch = useCallback(
    (clickedValue: string, category: string) => {
      track('recommended-search-clicked', {
        caterer_id: catererId,
        misc_json: buildMiscJson({category, clickedValue}),
        page: window.location.pathname,
      });
    },
    [buildMiscJson, catererId, track],
  );

  // Clicked a typeahead result (cuisine, business, or caterer) in the What panel.
  const trackTypeaheadClick = useCallback(
    ({suggestion, rank, keyword, suggestionCount}: TypeaheadClickArgs) => {
      track('clicked-typeahead-suggestion', {
        caterer_id: catererId,
        misc_json: buildMiscJson({
          clickedCatererPath: suggestion.type === TypeaheadDocType.Caterer ? suggestion.id : null,
          clickedSuggestionRank: rank,
          clickedValue: suggestion.display,
          keyword,
          suggestionCount,
          type: suggestion.type,
        }),
        page: window.location.pathname,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track],
  );

  // Typeahead API returned results while the What panel is open (debounced by the caller).
  const trackTypeaheadViewed = useCallback(
    ({caterers, cuisines, diversity, keyword, suggestionCount}: TypeaheadViewedArgs) => {
      track('viewed-typeahead-suggestion', {
        caterer_id: catererId,
        misc_json: buildMiscJson({
          catererSuggestions: caterers.map(result => result.id),
          cuisineSuggestions: cuisines.map(result => result.id),
          diversitySuggestions: diversity.map(result => result.id),
          keyword,
          suggestion_count: suggestionCount,
        }),
        page: window.location.pathname,
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackDraftOrderSelected = useCallback(
    (draftCatererId: string, source: 'what') => {
      trackClick('app-bar-draft-order-card', {
        misc_json: buildMiscJson({caterer_id: draftCatererId, source}),
      });
    },
    [buildMiscJson, trackClick],
  );

  // The `selected*` keys describe what the click actually restores — they mirror the base's
  // address/date/time/name fields, which still report the committed (pre-click) detail. A What cell
  // restores the whole detail, so it reports all of them; a Where cell restores only the address, so
  // reporting the entry's date/time/headcount there would claim values the bar never took.
  const trackSearchHistorySelected = useCallback(
    ({entry, rank, resultCount, source}: SearchHistorySelectedArgs) => {
      const {address} = entry;
      const restoredDetail =
        source === 'where'
          ? {}
          : {
              selectedEventDate: entry.eventOn,
              selectedEventDateTimeLocal: formatEventTimeForTracking({
                eventOn: entry.eventOn,
                eventLocalTime: entry.eventLocalTime,
                timeZoneIdentifier: address.timeZoneIdentifier,
              }),
              selectedEventName: entry.eventName,
              selectedEventTime: entry.eventLocalTime,
              selectedHeadcount: entry.headcount,
              selectedStrategy: entry.strategy,
            };
      trackClick('app-bar-search-history-card', {
        misc_json: buildMiscJson({
          rank,
          resultCount,
          selectedCity: address.city,
          // Identifies the clicked cell rather than a restored value, so both panels report it.
          selectedFulfillmentDetailId: entry.fulfillmentDetailId,
          selectedState: address.state,
          selectedStreet: address.street,
          ...restoredDetail,
          source,
        }),
      });
    },
    [buildMiscJson, trackClick],
  );

  // The Update buttons track before their (async) commit, so the base reports the pre-click
  // detail — these overlay the staged values, as trackSearchButton does. Where alone tracks after
  // its search guard, so an address it geocoded (never in `whereSelection`) overrides stale parts.
  const trackWhereUpdate = useCallback(
    (resolvedSelection?: WhereSelection) =>
      trackClick('event-bar-v2-where-update-button', {
        misc_json: buildMiscJson({
          ...stagedUpdateProperties,
          ...stagedAddressProperties(resolvedSelection ?? null),
        }),
      }),
    [buildMiscJson, stagedUpdateProperties, trackClick],
  );

  const trackWhenUpdate = useCallback(
    () =>
      trackClick('event-bar-v2-when-update-button', {
        misc_json: buildMiscJson(stagedUpdateProperties),
      }),
    [buildMiscJson, stagedUpdateProperties, trackClick],
  );

  const trackWhoUpdate = useCallback(
    () =>
      trackClick('event-bar-v2-who-update-button', {
        misc_json: buildMiscJson(stagedUpdateProperties),
      }),
    [buildMiscJson, stagedUpdateProperties, trackClick],
  );

  // Selected a typed/autocomplete address in the Where panel.
  const trackAddressSelected = useCallback(
    (searchKeyword: string, address: SelectedAddress) => {
      track('address selected', {
        caterer_id: catererId,
        misc_json: buildMiscJson(address),
        page: window.location.pathname,
        search_keyword: searchKeyword,
        sub_category: 'navigation bar',
        url: window.location.href,
        value: 'autocomplete address',
      });
    },
    [buildMiscJson, catererId, track],
  );

  // Selected a saved address in the Where panel.
  const trackSavedAddressSelected = useCallback(
    (searchKeyword: string, address: SelectedAddress) => {
      track('saved address selected', {
        caterer_id: catererId,
        misc_json: buildMiscJson(address),
        page: window.location.pathname,
        search_keyword: searchKeyword,
        sub_category: 'navigation bar',
        url: window.location.href,
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackRecentAddressSelected = useCallback(
    (searchKeyword: string, address: SelectedAddress) => {
      track('address selected', {
        caterer_id: catererId,
        misc_json: buildMiscJson(address),
        page: window.location.pathname,
        search_keyword: searchKeyword,
        sub_category: 'navigation bar',
        url: window.location.href,
        value: 'recently used address',
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackOrderType = useCallback(
    (orderType: FulfillmentDetailStrategy) => {
      track('order-type-updated', {
        caterer_id: catererId,
        misc_json: buildMiscJson({orderType}),
        page: window.location.pathname,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackOrderAccount = useCallback(
    ({corpAccountId, isParent}: OrderAccountArgs) => {
      track('order-account-updated', {
        caterer_id: catererId,
        misc_json: buildMiscJson({corpAccountId, isParent}),
        page: window.location.pathname,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackDateSelected = useCallback(
    (eventDate: string | null) => {
      track('date-selected', {
        caterer_id: catererId,
        misc_json: buildMiscJson({eventDate}),
        page: window.location.pathname,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackTimeSelected = useCallback(
    (eventTime: string | null) => {
      track('time-selected', {
        caterer_id: catererId,
        misc_json: buildMiscJson({eventTime}),
        page: window.location.pathname,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackHeadcount = useCallback(
    (headcount: number) => {
      track('headcount-updated', {
        caterer_id: catererId,
        misc_json: buildMiscJson({headcountNumber: headcount}),
        page: window.location.pathname,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackEventName = useCallback(
    (eventName: string) => {
      track('event-name-updated', {
        caterer_id: catererId,
        misc_json: buildMiscJson({eventName}),
        page: window.location.pathname,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackDietary = useCallback(
    (dietaryOptions: DietaryOptionEnum[]) => {
      track('dietary-preferences-selected', {
        caterer_id: catererId,
        misc_json: buildMiscJson({dietaryOptions}),
        page: window.location.pathname,
        sub_category: 'navigation bar',
      });
    },
    [buildMiscJson, catererId, track],
  );

  const trackSearchButton = useCallback(() => {
    track('search-button-clicked', {
      caterer_id: catererId,
      misc_json: buildMiscJson(stagedSearchProperties),
      page: window.location.pathname,
      sub_category: 'navigation bar',
    });
  }, [buildMiscJson, catererId, stagedSearchProperties, track]);

  const trackSearchIconHovered = useCallback(() => {
    track('search-icon-hovered', {
      misc_json: buildMiscJson(),
      page: window.location.pathname,
      sub_category: 'navigation bar',
    });
  }, [buildMiscJson, track]);

  return {
    trackFieldOpened,
    trackRecommendedSearch,
    trackTypeaheadClick,
    trackTypeaheadViewed,
    trackDraftOrderSelected,
    trackSearchHistorySelected,
    trackWhereUpdate,
    trackWhenUpdate,
    trackWhoUpdate,
    trackAddressSelected,
    trackSavedAddressSelected,
    trackRecentAddressSelected,
    trackOrderType,
    trackOrderAccount,
    trackDateSelected,
    trackTimeSelected,
    trackHeadcount,
    trackEventName,
    trackDietary,
    trackSearchButton,
    trackSearchIconHovered,
  };
};

export default useEventBarV2Tracking;
